import { h } from 'preact'
import { useRef, useEffect } from 'preact/compat'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import LinkButton from '../LinkButton'
import CloseIcon from '../../../assets/icons/icons-close.svg'
import './styles.scss'

function Modal({
  children,
  onCancel = () => {},
  onConfirm = null,
  withControls = true,
  className = '',
  contentClassName = '',
  title = '',
  cancelButtonText = 'Cancel',
  confirmButtonText = 'OK',
  onlyIcon = false,
  isOpened = false,
  showIcon = true,
  isClickableOutside = true
}) {
  const wrapperRef = useRef()
  const handleClickOutside = e => {
    if (isClickableOutside && e.target === wrapperRef.current) {
      onCancel()
    }
  }

  const handleCancel = e => {
    e.preventDefault()
    onCancel()
  }

  const handleConfirm = e => {
    e.preventDefault()
    if (onConfirm) {
      onConfirm()
    }
  }

  useEffect(() => {
    document.body.style.overflow = isOpened ? 'hidden' : 'auto'
  }, [isOpened])

  useEffect(() => {
    return () => (document.body.style.overflow = 'auto')
  }, [])

  useEffect(() => {
    if (!wrapperRef.current) {
      return
    }
    wrapperRef.current.addEventListener('mousedown', handleClickOutside)
    return () => {
      wrapperRef.current.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef, isClickableOutside])

  const modalWrapperClasses = classNames('modal-wrapper', { hidden: !isOpened })

  const modalClasses = classNames('modal', className)
  const contentClasses = classNames('modal__content', contentClassName)

  return (
    <div
      ref={wrapperRef}
      className={modalWrapperClasses}
      data-testid="modal-element"
    >
      <div className={modalClasses}>
        {(!!title.length || onlyIcon) && (
          <header className="modal__title">
            <div className="modal__header-area">
              <span>{title}</span>
              {showIcon && (
                <button
                  type="button"
                  id="modal-close-button"
                  className="modal__close"
                  onClick={handleCancel}
                >
                  <img src={CloseIcon} alt="Close button" />
                </button>
              )}
            </div>
          </header>
        )}

        <div className={contentClasses}>{children}</div>

        {withControls && (
          <footer className="modal__footer">
            <LinkButton
              id="modal-cancel-button"
              data-testid="modal-element-cancel"
              title={cancelButtonText}
              onClick={handleCancel}
            />
            <button
              className="button primary medium"
              type="button"
              id="modal-confirm-button"
              data-testid="modal-element-confirm"
              onClick={handleConfirm}
            >
              {confirmButtonText}
            </button>
          </footer>
        )}
      </div>
    </div>
  )
}

Modal.propTypes = {
  onCancel: PropTypes.func,
  children: PropTypes.element.isRequired,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  contentClassName: PropTypes.string,
  withControls: PropTypes.bool,
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  cancelButtonText: PropTypes.string,
  confirmButtonText: PropTypes.string,
  isOpened: PropTypes.bool,
}

export default Modal
